import { type Signal, signal } from "@preact/signals";
import { ReactNode } from "preact/compat";
import { useEffect, useMemo, useState } from "preact/hooks";
import { ClipLoader } from "react-spinners";
import timeago from "epoch-timeago";
import SyntaxHighlighter from "react-syntax-highlighter";
import {
  github as lightCodeStyle,
  hybrid as darkCodeStyle,
} from "react-syntax-highlighter/dist/esm/styles/hljs/index.js";
import { AlertTriangle, RefreshCw } from "lucide-preact";
import { SocialIcon } from "react-social-icons";
import { extractErrorMessage, logErrorToConsole } from "./notify.ts";
import { isDarkMode } from "./routes.tsx";
import { primaryColor, t } from "./theme.ts";
import { normalizeTimestamp } from "../../src/timeUtils.ts";

export const useLiveTimeAgo = (timeMs: number): string => {
  const normTime = normalizeTimestamp(timeMs);
  const [str, setStr] = useState(() => normTime > 0 ? timeago(normTime) : "-");

  useEffect(() => {
    if (normTime <= 0) return;
    let intervalMs = 60000;
    const diff = Date.now() - normTime;
    if (diff < 60000) intervalMs = 10000;

    const timer = setInterval(() => {
      setStr(timeago(normTime));
    }, intervalMs);

    return () => clearInterval(timer);
  }, [normTime]);

  return str;
};

export const LiveTimeAgo = ({ time }: { time: number }) => {
  const str = useLiveTimeAgo(time);
  return <>{str}</>;
};

export const NumberInput = ({
  name,
  signal,
}: {
  name: string;
  signal: Signal<number>;
}) => (
  <label style={{ display: "flex", flexDirection: "column" }}>
    {name}
    &nbsp;
    <input
      className="input-field"
      type="number"
      id={name}
      name={name}
      min={1}
      onInput={(event) => {
        const value = (event.target as HTMLInputElement).value;
        if (value) {
          const n = Number.parseInt(value);
          if (n >= 1) signal.value = n;
        }
      }}
      value={signal.value}
    />
  </label>
);

export const TextInput = ({
  name,
  signal,
  placeholder,
}: {
  placeholder?: string;
  name: string;
  signal: Signal<string>;
}) => (
  <label style={{ display: "flex", flexDirection: "column" }}>
    {name}
    &nbsp;
    <input
      className="input-field"
      placeholder={placeholder}
      type="text"
      value={signal}
      onInput={(event) => {
        signal.value = (event.target as HTMLInputElement).value;
      }}
    />
  </label>
);

export const Select = ({
  name,
  options,
  signal,
}: {
  name: string;
  options: string[];
  signal: Signal<string>;
}) => (
  <label style={{ display: "flex", flexDirection: "column" }}>
    {name}
    &nbsp;
    <select
      value={signal.value}
      onChange={(event) => {
        signal.value = (event.target as HTMLSelectElement).value;
      }}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </label>
);

export const SelectInput = ({
  name,
  value,
  onChange,
  options,
}: {
  name: string;
  value: string;
  onChange: (v: string) => void;
  options: { id: string; name: string }[];
}) => {
  const [filter, setFilter] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const th = t();

  const filteredOptions = options.filter((o) =>
    o.name.toLowerCase().includes(filter.toLowerCase())
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [filter]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredOptions[highlightedIndex]) {
        const opt = filteredOptions[highlightedIndex];
        onChange(opt.id);
        setIsOpen(false);
        setFilter("");
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const selectedName = options.find((o) => o.id === value)?.name ?? "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        marginBottom: 10,
      }}
      onKeyDown={handleKeyDown}
    >
      <label>{name}</label>
      <div
        className="select-field"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: 22,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedName || "Select..."}
        </span>
        <span style={{ fontSize: 10, marginLeft: 8 }}>
          {isOpen ? "▲" : "▼"}
        </span>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: th.inputBg,
            border: `1px solid ${th.inputBorder}`,
            borderRadius: 6,
            marginTop: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            maxHeight: 300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <input
            autoFocus
            className="input-field"
            style={{
              margin: 8,
              width: "calc(100% - 16px)",
              boxSizing: "border-box",
            }}
            placeholder="Type to filter..."
            value={filter}
            onInput={(e) => setFilter(e.currentTarget.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filteredOptions.length > 0
              ? (
                filteredOptions.map((opt, index) => (
                  <div
                    key={opt.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt.id);
                      setIsOpen(false);
                      setFilter("");
                    }}
                    style={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      background: index === highlightedIndex
                        ? th.hoverBg
                        : "transparent",
                      color: index === highlightedIndex ? th.primary : th.text,
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    {opt.name}
                  </div>
                ))
              )
              : (
                <div style={{ padding: "8px 12px", color: th.textDim }}>
                  No matches
                </div>
              )}
          </div>
        </div>
      )}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
      )}
    </div>
  );
};

export const useIsMobile = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? globalThis.window.innerWidth : 1024,
  );
  useEffect(() => {
    const handleWindowSizeChange = () => {
      setWidth(globalThis.window.innerWidth);
    };
    globalThis.window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      globalThis.window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  return width <= 768;
};

export const ErrorMessage = ({
  error,
  title,
  action,
}: {
  error?: { message?: string } | Error | string | null;
  title?: string;
  action?: ReactNode;
}) => {
  const th = t();
  const rawMessage = extractErrorMessage(error);

  useEffect(() => {
    logErrorToConsole(error, "ErrorMessage");
    try {
      const posthog = (globalThis as Record<string, unknown>)._posthog as
        | {
          capture: (
            event: string,
            properties?: Record<string, unknown>,
          ) => void;
        }
        | undefined;
      posthog?.capture("Application Error", {
        message: rawMessage,
        url: typeof window !== "undefined" ? globalThis.location.href : "",
      });
    } catch {
      // Ignore analytics capture errors
    }
  }, [error, rawMessage]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        gap: 16,
        textAlign: "center",
        padding: "40px 24px",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: th.dangerBg || "rgba(239, 68, 68, 0.12)",
          border: `1px solid ${th.dangerBorder || "rgba(239, 68, 68, 0.3)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: th.danger || "#ef4444",
        }}
      >
        <AlertTriangle size={28} />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          maxWidth: 500,
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: th.textBright,
            letterSpacing: "-0.02em",
          }}
        >
          {title || "Something went wrong"}
        </div>
        <div
          style={{
            color: th.textMuted,
            fontSize: 15,
            lineHeight: 1.5,
          }}
        >
          An error has occurred. Try again later or talk to us on Discord.
        </div>
      </div>

      {rawMessage && (
        <div
          style={{
            background: th.cardBg,
            border: `1px solid ${th.border}`,
            padding: "10px 16px",
            borderRadius: 8,
            fontFamily: "monospace",
            fontSize: 13,
            color: th.textDim,
            maxWidth: 540,
            width: "100%",
            boxSizing: "border-box",
            wordBreak: "break-word",
            textAlign: "left",
            lineHeight: 1.4,
          }}
        >
          <span style={{ color: th.danger, fontWeight: 600 }}>Error:</span>{" "}
          {rawMessage}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          justifyContent: "center",
          marginTop: 8,
        }}
      >
        <button
          type="button"
          className="compact-button"
          style={{
            fontSize: 14,
            padding: "8px 18px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontWeight: 600,
            cursor: "pointer",
          }}
          onClick={() => globalThis.location.reload()}
        >
          <RefreshCw size={15} />
          Try Again
        </button>

        <a
          href="https://discord.gg/4taKG4KTVJ"
          target="_blank"
          rel="noopener noreferrer"
          className="compact-button"
          style={{
            fontSize: 14,
            padding: "8px 18px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          <SocialIcon
            url="https://discord.gg/4taKG4KTVJ"
            bgColor="transparent"
            fgColor="currentColor"
            style={{ height: 18, width: 18 }}
          />
          Chat on Discord
        </a>

        {action}
      </div>
    </div>
  );
};

export const isUuid = (str: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

export const InvalidIdError = ({ id, type }: { id: string; type: string }) => {
  const th = t();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        color: primaryColor(),
        gap: 16,
        textAlign: "center",
        padding: "40px 24px",
      }}
    >
      <div style={{ fontSize: 80, lineHeight: 1 }}>😕</div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>Invalid {type} ID</div>
      <div
        style={{
          color: th.textMuted,
          fontSize: 16,
          maxWidth: 500,
          lineHeight: 1.5,
        }}
      >
        The ID{" "}
        <code
          style={{
            background: th.inputBg,
            padding: "2px 6px",
            borderRadius: 4,
            fontFamily: "monospace",
            fontSize: 14,
          }}
        >
          {id}
        </code>{" "}
        is not in a valid format. Expected a UUID, but received an invalid
        identifier.
      </div>
      <button
        type="button"
        className="compact-button"
        style={{ fontSize: 16, padding: "8px 24px", marginTop: 8 }}
        onClick={() => (globalThis.location.href = "/")}
      >
        Go to Home
      </button>
    </div>
  );
};

export const CodeBlock = ({ code }: { code: string }) => {
  const th = t();
  const dark = isDarkMode.value;
  return (
    <SyntaxHighlighter
      language="typescript"
      customStyle={{
        flex: 1,
        textAlign: "left",
        background: th.cardBg,
        borderRadius: 8,
      }}
      style={dark ? darkCodeStyle : lightCodeStyle}
    >
      {code}
    </SyntaxHighlighter>
  );
};

export const Loading = (
  { text: _text, fullPage }: { text?: string; fullPage?: boolean },
) => (
  <div
    style={{
      display: "flex",
      flexGrow: 1,
      justifyContent: "center",
      alignItems: "center",
      gap: 10,
      flexDirection: "column",
      ...(fullPage
        ? {
          minHeight: "calc(100vh - 120px)",
          width: "100%",
        }
        : {
          width: "100%",
          height: "100%",
          minHeight: "180px",
        }),
    }}
  >
    <ClipLoader color={primaryColor()} />
  </div>
);

export const stringToColor = (
  text: string,
  saturation: number,
  lightness: number,
) => {
  // FNV-1a hash for better distribution
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const hue = (hash >>> 0) % 360;
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const PageContainer = (
  { children, wide, fullWidth }: {
    children: ReactNode;
    wide?: boolean;
    fullWidth?: boolean;
  },
) => (
  <div
    style={{
      width: "100%",
      margin: "0 auto",
      height: "100%",
      display: "flex",
    }}
  >
    <div
      style={{
        scrollbarGutter: fullWidth ? "auto" : "stable",
        maxWidth: fullWidth ? "100%" : wide ? 1200 : 580,
        margin: "0 auto",
        height: "100%",
        overflowX: "hidden",
        display: "flex",
        position: "relative",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
      }}
    >
      {children}
    </div>
  </div>
);

export const obfuscateIdsSignal = signal<boolean>(false);

export const looksLikeId = (str: string) => {
  if (!str) return false;
  // UUID
  if (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
  ) return true;
  // Phone numbers with optional prefix
  if (/^(whatsapp:)?\+?\d{10,15}$/i.test(str)) return true;
  // IDs that are 16+ alphanumeric characters and contain both letters and numbers
  if (/^(?=.*[0-9])(?=.*[a-zA-Z])[a-zA-Z0-9_-]{16,}$/.test(str)) return true;
  // IDs with common prefixes like usr_, bot_, org_, etc.
  if (/^[a-z]{3,}_[a-zA-Z0-9]{8,}$/i.test(str)) return true;
  // Hex strings 12+ chars long
  if (/^[0-9a-f]{12,}$/i.test(str)) return true;
  // Email address
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)) return true;
  return false;
};

export const ObfuscatedText = (
  { text, maxLength }: { text: string; maxLength?: number },
) => {
  const display = maxLength ? text.slice(0, maxLength) : text;
  if (!obfuscateIdsSignal.value || !looksLikeId(text)) {
    return <>{display}</>;
  }
  return (
    <span className="obfuscated-text" title="Hover to reveal">
      {display}
    </span>
  );
};

export const formatLastActivity = (ts?: number) => {
  if (!ts) return "no activity";
  const days = Math.floor((Date.now() - ts) / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "1d ago";
  return `${days}d ago`;
};

type GroupWithActivity = {
  id: string;
  name: string;
  lastActivityAt?: number;
};

export const GroupSelector = ({
  groups,
  value,
  onChange,
  includeAll,
}: {
  groups: GroupWithActivity[];
  value: string;
  onChange: (v: string) => void;
  includeAll?: boolean;
}) => {
  const options = useMemo(() => {
    const sorted = [...groups].sort(
      (a, b) => (b.lastActivityAt ?? 0) - (a.lastActivityAt ?? 0),
    );
    const mapped = sorted.map((g) => ({
      id: g.id,
      name: `${g.name} (${formatLastActivity(g.lastActivityAt)})`,
    }));
    return includeAll ? [{ id: "", name: "All groups" }, ...mapped] : mapped;
  }, [groups, includeAll]);

  return (
    <SelectInput
      name="Group"
      value={value}
      onChange={onChange}
      options={options}
    />
  );
};
